import {
  Inject,
  Injectable,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import {
  addDocumentMetadata,
  Backend,
  BackendConfigs,
  BackendPushSuccessResponse,
  BackendSuccessResponse,
  BACKEND_DEFAULT_BATCH_SIZE,
  DatastoreCollectionType,
  DatastorePushCollectionType,
  DatastoreSetCollectionType,
  deepSpread,
  mergeRawDocuments,
  OrderByDirection,
  Ordering,
  RecursivePartial,
  Reference,
  StoreBackend,
  StoreBackendInterface,
} from '@freelancer/datastore/core';
import * as Rx from 'rxjs';
import { DatastoreFakeConfig, DATASTORE_FAKE_CONFIG } from './datastore.config';
import { debugConsoleLog, debugConsoleWarn } from './datastore.helpers';
import { FakeStoreState } from './store.model';

@Injectable()
export class StoreBackendFake implements StoreBackendInterface {
  // FIXME: Consider saving this in session storage
  private storeStateSubject$: Rx.BehaviorSubject<
    FakeStoreState
  > = new Rx.BehaviorSubject({});
  storeState$: Rx.Observable<
    FakeStoreState
  > = this.storeStateSubject$.asObservable();

  constructor(
    @Inject(DATASTORE_FAKE_CONFIG) private datastoreConfig: DatastoreFakeConfig,
  ) {}

  defaultOrder<C extends DatastoreCollectionType>(
    collection: C['Name'],
  ): Ordering<C> {
    return [
      {
        field: 'id' as any,
        direction: OrderByDirection.DESC,
      },
    ];
  }

  batchSize<C extends DatastoreCollectionType>(ref: Reference<C>): number {
    return BACKEND_DEFAULT_BATCH_SIZE;
  }

  push<C extends DatastoreCollectionType & DatastorePushCollectionType>(
    { path: { collection, authUid } }: Reference<C>,
    document: C['DocumentType'],
  ): Rx.Observable<BackendPushSuccessResponse<C>> {
    debugConsoleLog(
      this.datastoreConfig,
      `Pushing to '${collection}' document`,
      document,
    );

    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      this.storeStateSubject$.next({
        ...storeState,
        [collection]: {
          [authUid]: {
            documents: addDocumentMetadata([document]),
          },
        },
      });
    } else {
      const userCollectionSlice = collectionSlice[authUid];

      if (userCollectionSlice === undefined) {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: addDocumentMetadata([document]),
            },
          },
        });
      } else {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: mergeRawDocuments(
                userCollectionSlice.documents,
                addDocumentMetadata([document]),
              ),
            },
          },
        });
      }
    }

    return Rx.of({
      status: 'success',
      id: (document as any).id,
    });
  }

  set<C extends DatastoreCollectionType & DatastoreSetCollectionType>(
    { path: { collection, authUid } }: Reference<C>,
    id: number | string,
    document: C['DocumentType'],
  ): Rx.Observable<BackendSuccessResponse> {
    debugConsoleLog(
      this.datastoreConfig,
      `Setting '${id}' in '${collection}' document`,
      document,
    );

    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      this.storeStateSubject$.next({
        ...storeState,
        [collection]: {
          [authUid]: {
            documents: addDocumentMetadata([document]),
          },
        },
      });
    } else {
      const userCollectionSlice = collectionSlice[authUid];

      if (userCollectionSlice === undefined) {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: addDocumentMetadata([document]),
            },
          },
        });
      } else {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: mergeRawDocuments(
                userCollectionSlice.documents,
                addDocumentMetadata([document]),
              ),
            },
          },
        });
      }
    }

    return Rx.of({ status: 'success' });
  }

  update<C extends DatastoreCollectionType>(
    { path: { collection, authUid } }: Reference<C>,
    id: number | string,
    delta: RecursivePartial<C['DocumentType']>,
  ): Rx.Observable<BackendSuccessResponse> {
    debugConsoleLog(
      this.datastoreConfig,
      `Updating '${id}' in ${collection} with delta`,
      delta,
    );

    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        "Trying to merge into a collection that doesn't exist. Skipping",
      );
      return Rx.of({ status: 'success' });
    }

    const userCollectionSlice = collectionSlice[authUid];
    if (userCollectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        "Trying to merge into a collection that doesn't exist for this user. Skipping",
      );
      return Rx.of({ status: 'success' });
    }

    const originalDocument = userCollectionSlice.documents[id].rawDocument;

    this.storeStateSubject$.next({
      ...storeState,
      [collection]: {
        ...collectionSlice,
        [authUid]: {
          documents: mergeRawDocuments(
            userCollectionSlice.documents,
            addDocumentMetadata([deepSpread(originalDocument, delta)]),
          ),
        },
      },
    });

    return Rx.of({ status: 'success' });
  }

  delete<C extends DatastoreCollectionType>(
    { path: { collection, authUid } }: Reference<C>,
    id: number | string,
  ): Rx.Observable<BackendSuccessResponse> {
    debugConsoleLog(
      this.datastoreConfig,
      `Deleting '${id}' from ${collection}.`,
    );

    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        `Trying to delete from '${collection}' which doesn't exist. Skipping`,
      );
      return Rx.of({ status: 'success' });
    }

    const userCollectionSlice = collectionSlice[authUid];
    if (userCollectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        `Trying to delete from '${collection}' which doesn't exist for user ${authUid}. Skipping`,
      );
      return Rx.of({ status: 'success' });
    }

    const documents = { ...userCollectionSlice.documents };
    delete documents[id];

    this.storeStateSubject$.next({
      ...storeState,
      [collection]: {
        ...collectionSlice,
        [authUid]: {
          documents,
        },
      },
    });
    return Rx.of({ status: 'success' });
  }

  reset<C extends DatastoreCollectionType>(
    authUid: string,
    collection?: C['Name'],
    id?: number | string,
  ) {
    const storeState = this.storeStateSubject$.getValue();

    if (!collection) {
      this.storeStateSubject$.next({});
      debugConsoleLog(
        this.datastoreConfig,
        'Resetting the store to an empty state.',
      );
      return;
    }

    const collectionSlice = storeState[collection];
    if (collectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        `Trying to reset '${collection}' which doesn't exist. Skipping`,
      );
      return;
    }

    this.storeStateSubject$.next(
      collection
        ? {
            ...storeState,
            [collection]: {
              ...collectionSlice,
              [authUid]: {
                documents: {},
              },
            },
          }
        : {},
    );
  }

  addFeature<C extends DatastoreCollectionType>(
    collectionName: any, // FIXME
    requestFactory: BackendConfigs[any], // FIXME
  ): void {
    // Used by BackendFeatureModule but not implemented since there's no backend
  }
}

type BackendConfigFactory<C extends DatastoreCollectionType> = () => Backend<C>;

@NgModule({})
export class BackendRootModule {}

@NgModule({})
export class BackendFeatureModule {}

@NgModule({})
export class BackendFakeModule {
  static forRoot(): ModuleWithProviders<BackendRootModule> {
    return {
      ngModule: BackendRootModule,
      providers: [
        StoreBackendFake,
        { provide: StoreBackend, useExisting: StoreBackendFake }, // BackendModule.forFeature
      ],
    };
  }

  static forFeature<C extends DatastoreCollectionType>(
    collectionName: C['Name'],
    configFactory: BackendConfigFactory<C>,
  ): ModuleWithProviders<BackendFeatureModule> {
    return {
      ngModule: BackendFeatureModule,
    };
  }
}
